import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Grid, Card, CardContent, Avatar, Chip, Button, Tabs, Tab, Dialog, DialogTitle, DialogContent,
  DialogActions, TextField, Snackbar, Alert, Autocomplete, LinearProgress,
} from '@mui/material';
import {
  AssignmentTurnedIn as ApprovedIcon, HourglassTop as PendingIcon, Undo as ReturnedIcon,
  Groups as StudentsIcon, ListAlt as TotalIcon,
} from '@mui/icons-material';
import { logbookAPI, studentAPI } from '../services/api';

const STATUS = { APPROVED: { label: 'Approved', color: 'success' }, SUBMITTED: { label: 'Awaiting review', color: 'warning' }, RETURNED: { label: 'Returned', color: 'error' } };
const AS = { OBSERVED: 'Observed', ASSISTED: 'Assisted', PERFORMED: 'Performed' };

function StatCard({ icon, label, value, color }) {
  return (
    <Card>
      <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <Avatar sx={{ bgcolor: `${color}1f`, color, width: 48, height: 48 }}>{icon}</Avatar>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700, lineHeight: 1.1 }}>{value}</Typography>
          <Typography variant="body2" color="text.secondary">{label}</Typography>
        </Box>
      </CardContent>
    </Card>
  );
}

export default function LogBookManagement() {
  const [tab, setTab] = useState(0);
  const [summary, setSummary] = useState({ total: 0, approved: 0, submitted: 0, returned: 0, studentsLogging: 0, byCategory: {} });
  const [pending, setPending] = useState([]);
  const [students, setStudents] = useState([]);
  const [picked, setPicked] = useState(null);
  const [book, setBook] = useState(null);
  const [review, setReview] = useState(null);   // { entry, status, remarks }
  const [toast, setToast] = useState({ open: false, message: '', severity: 'success' });

  const notify = (message, severity = 'success') => setToast({ open: true, message, severity });
  const errorText = (e, fallback) => e?.response?.data?.message || fallback;

  const load = async () => {
    try {
      const [s, p, st] = await Promise.all([logbookAPI.getSummary(), logbookAPI.getPending(), studentAPI.getAll()]);
      setSummary(s.data); setPending(p.data); setStudents(st.data);
    } catch (e) { notify(errorText(e, 'Could not load the log book'), 'error'); }
  };
  useEffect(() => { load(); }, []);

  const openStudent = async (s) => {
    setPicked(s); setBook(null);
    if (!s) return;
    try { setBook((await logbookAPI.getForStudent(s.id)).data); }
    catch (e) { notify(errorText(e, 'Could not load this student'), 'error'); }
  };

  const submitReview = async () => {
    if (review.status === 'RETURNED' && !review.remarks.trim()) return notify('Add a remark explaining what to correct', 'error');
    try {
      await logbookAPI.review(review.entry.id, { status: review.status, remarks: review.remarks });
      setReview(null); notify(review.status === 'APPROVED' ? 'Entry approved' : 'Entry returned to the student');
      load(); if (picked) openStudent(picked);
    } catch (e) { notify(errorText(e, 'Could not save the review'), 'error'); }
  };

  const categories = Object.entries(summary.byCategory || {});
  const maxCat = Math.max(1, ...categories.map(([, n]) => n));

  return (
    <Box>
      <Typography variant="h4" sx={{ mb: 3 }}>Skills Log Book</Typography>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={6} md={2.4}><StatCard icon={<TotalIcon />} label="Entries" value={summary.total} color="#00796b" /></Grid>
        <Grid item xs={6} md={2.4}><StatCard icon={<PendingIcon />} label="Awaiting review" value={summary.submitted} color="#ef6c00" /></Grid>
        <Grid item xs={6} md={2.4}><StatCard icon={<ApprovedIcon />} label="Approved" value={summary.approved} color="#2e7d32" /></Grid>
        <Grid item xs={6} md={2.4}><StatCard icon={<ReturnedIcon />} label="Returned" value={summary.returned} color="#c62828" /></Grid>
        <Grid item xs={12} md={2.4}><StatCard icon={<StudentsIcon />} label="Students logging" value={summary.studentsLogging} color="#1565c0" /></Grid>
      </Grid>

      <Paper sx={{ mb: 2 }}>
        <Tabs value={tab} onChange={(e, v) => setTab(v)} sx={{ px: 2, borderBottom: '1px solid #eee' }}>
          <Tab label={`Awaiting review (${pending.length})`} />
          <Tab label="By student" />
          <Tab label="By clinical area" />
        </Tabs>
      </Paper>

      {tab === 0 && (
        <Paper>
          <TableContainer>
            <Table size="small">
              <TableHead><TableRow>
                <TableCell>Student</TableCell><TableCell>Program / Year</TableCell><TableCell>Procedure</TableCell><TableCell>Area</TableCell>
                <TableCell>Date</TableCell><TableCell>As</TableCell><TableCell align="right">Review</TableCell>
              </TableRow></TableHead>
              <TableBody>
                {pending.length === 0 && <TableRow><TableCell colSpan={7} align="center" sx={{ py: 4, color: 'text.secondary' }}>Nothing is waiting for review</TableCell></TableRow>}
                {pending.map(e => (
                  <TableRow key={e.id} hover>
                    <TableCell sx={{ fontWeight: 600 }}>{e.studentName}<Typography variant="caption" display="block" color="text.secondary">{e.admissionNo}</Typography></TableCell>
                    <TableCell>{e.program || '-'}</TableCell>
                    <TableCell>{e.procedureName}<Typography variant="caption" display="block" color="text.secondary">{e.category}</Typography></TableCell>
                    <TableCell>{e.department || '-'}</TableCell>
                    <TableCell>{e.procedureDate}</TableCell>
                    <TableCell>{AS[e.performedAs]}</TableCell>
                    <TableCell align="right">
                      <Button size="small" color="success" onClick={() => setReview({ entry: e, status: 'APPROVED', remarks: '' })}>Approve</Button>
                      <Button size="small" color="error" onClick={() => setReview({ entry: e, status: 'RETURNED', remarks: '' })}>Return</Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {tab === 1 && (
        <Paper sx={{ p: 2 }}>
          <Autocomplete options={students} value={picked} onChange={(e, v) => openStudent(v)} sx={{ maxWidth: 460, mb: 2 }}
            getOptionLabel={s => `${s.firstName} ${s.lastName} (${s.admissionNo})`}
            renderInput={params => <TextField {...params} label="Choose a student" size="small" />} />
          {picked && !book && <LinearProgress />}
          {book && (
            <>
              <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
                <Chip label={`${book.summary.total} entries`} />
                <Chip color="success" label={`${book.summary.approved} approved`} />
                <Chip color="warning" label={`${book.summary.submitted} awaiting review`} />
                <Chip color="error" label={`${book.summary.returned} returned`} />
              </Box>
              <TableContainer>
                <Table size="small">
                  <TableHead><TableRow>
                    <TableCell>Date</TableCell><TableCell>Procedure</TableCell><TableCell>Area</TableCell><TableCell>As</TableCell>
                    <TableCell>Status</TableCell><TableCell>Reviewer remarks</TableCell>
                  </TableRow></TableHead>
                  <TableBody>
                    {book.entries.length === 0 && <TableRow><TableCell colSpan={6} align="center" sx={{ py: 3, color: 'text.secondary' }}>No entries yet</TableCell></TableRow>}
                    {book.entries.map(e => (
                      <TableRow key={e.id} hover>
                        <TableCell>{e.procedureDate}</TableCell>
                        <TableCell>{e.procedureName}<Typography variant="caption" display="block" color="text.secondary">{e.category}</Typography></TableCell>
                        <TableCell>{e.department || '-'}</TableCell>
                        <TableCell>{AS[e.performedAs]}</TableCell>
                        <TableCell><Chip size="small" label={STATUS[e.status].label} color={STATUS[e.status].color} /></TableCell>
                        <TableCell>{e.reviewerRemarks ? `${e.reviewerRemarks}${e.reviewerName ? ` — ${e.reviewerName}` : ''}` : '-'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}
        </Paper>
      )}

      {tab === 2 && (
        <Paper sx={{ p: 3 }}>
          {categories.length === 0 && <Typography color="text.secondary">No entries yet</Typography>}
          {categories.map(([name, n]) => (
            <Box key={name} sx={{ mb: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>{name}</Typography>
                <Typography variant="body2">{n} {n === 1 ? 'entry' : 'entries'}</Typography>
              </Box>
              <LinearProgress variant="determinate" value={(n / maxCat) * 100} sx={{ height: 10, borderRadius: 5 }} />
            </Box>
          ))}
        </Paper>
      )}

      <Dialog open={!!review} onClose={() => setReview(null)} maxWidth="sm" fullWidth>
        <DialogTitle>{review?.status === 'APPROVED' ? 'Approve entry' : 'Return entry to student'}</DialogTitle>
        <DialogContent dividers>
          {review && (
            <>
              <Typography sx={{ fontWeight: 600 }}>{review.entry.procedureName}</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>{review.entry.studentName} · {review.entry.procedureDate} · {AS[review.entry.performedAs]}</Typography>
              {review.entry.caseSummary && <Alert severity="info" icon={false} sx={{ mb: 2 }}>{review.entry.caseSummary}</Alert>}
              <TextField fullWidth multiline minRows={2} label={review.status === 'APPROVED' ? 'Remarks (optional)' : 'What should the student correct?'}
                required={review.status === 'RETURNED'} value={review.remarks} onChange={e => setReview({ ...review, remarks: e.target.value })} />
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReview(null)}>Cancel</Button>
          <Button variant="contained" color={review?.status === 'APPROVED' ? 'success' : 'error'} onClick={submitReview}>{review?.status === 'APPROVED' ? 'Approve' : 'Return'}</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={toast.open} autoHideDuration={3500} onClose={() => setToast({ ...toast, open: false })}>
        <Alert severity={toast.severity} onClose={() => setToast({ ...toast, open: false })}>{toast.message}</Alert>
      </Snackbar>
    </Box>
  );
}
